/**
 * @module flow-list/components/FlowFilterBar
 *
 * The flows toolbar: search, favorites star, the primary filters
 * (Tags / Status / Health / Last run) as fixed-size checkbox menus, a sort menu, the
 * "More filters" dialog trigger, the result count, and the view switcher.
 *
 * Designed as a single "lockup" — in table view the toolbar fuses with the
 * table card below it (`attached`); in grouped view it stands alone. All
 * primary filters are visible in the bar, so there is no secondary chips row.
 * Long-tail filters (date range and author) live in the dialog — see
 * `WorkflowFilters.tsx`. The lockup shell and menu primitives are shared
 * with other list pages via `@chatsdet/ui` FilterToolbar.
 */
import React from 'react';
import { useGuideSurface } from '@runcofactor/guide';
import {
  Button,
  Download,
  FilterToolbar,
  FilterToolbarChips,
  FilterToolbarDivider,
  FilterToolbarSearch,
  Menu,
  MenuItem,
  MenuTrigger,
  PillToggle,
  Text,
  ToolbarFilterMenu,
  cn,
  type MultiSelectOption,
  type PillToggleOption,
} from '@chatsdet/ui';
import { ArrowsDownUp, Check, Rows, SquaresFour, Star } from '@phosphor-icons/react';
import type { FlowsSearchParams } from '../../../../router';
import type { Tag } from '../../../../hooks/useTags';
import type { FlowsViewMode } from '../../../../hooks/useUserSettings';
import { useEligibilityModel } from '../../../../hooks/useEligibilityModel';
import {
  LAST_RUN_STATUS_OPTIONS,
  HEALTH_OPTIONS,
  ELIGIBILITY_OPTIONS,
  PROMOTION_OPTIONS,
  eligibilityFromIds,
  eligibilitySelectedIds,
  STATUS_OPTIONS,
  type ActiveFilterChip,
} from '../../../components/workflows/WorkflowFilters';

/** One menu id per (sortBy, sortOrder) combination the UI offers. */
const SORT_CHOICES = [
  { id: 'created-desc', label: 'Newest', sortBy: undefined, sortOrder: undefined },
  { id: 'lastRunAt-desc', label: 'Recently run', sortBy: 'lastRunAt', sortOrder: 'desc' },
  { id: 'name-asc', label: 'Name A–Z', sortBy: 'name', sortOrder: 'asc' },
  { id: 'name-desc', label: 'Name Z–A', sortBy: 'name', sortOrder: 'desc' },
] as const;

type SortChoiceId = (typeof SORT_CHOICES)[number]['id'];

function currentSortChoice(sortBy?: string, sortOrder?: 'asc' | 'desc'): SortChoiceId {
  if (sortBy === 'lastRunAt') return 'lastRunAt-desc';
  if (sortBy === 'name') return sortOrder === 'desc' ? 'name-desc' : 'name-asc';
  return 'created-desc';
}

export type FlowFilterBarProps = {
  searchInput: string;
  onSearchInputChange: (value: string) => void;
  searchParams: FlowsSearchParams;
  onFilterChange: (updates: Partial<FlowsSearchParams>) => void;
  tags: Tag[];
  tagsLoading: boolean;
  /** "More filters" dialog trigger from useWorkflowFilters. */
  filterDialogTrigger: React.ReactNode;
  /** Every active filter as a little removable tag, shown in a row inside the lockup. */
  activeFilterChips: ActiveFilterChip[];
  onChipRemove: (chipId: string) => void;
  /** Clear search + every filter. Hidden when nothing is active. */
  onClearAll: () => void;
  showClearAll: boolean;
  /** Fuse with the card below (table view): square bottom corners, no bottom border. */
  attached?: boolean;
};

const VIEW_OPTIONS: readonly PillToggleOption<FlowsViewMode>[] = [
  { value: 'table', label: 'Table', icon: <Rows size={14} /> },
  { value: 'grouped', label: 'Groups', icon: <SquaresFour size={14} /> },
];

/**
 * The row that sits OUTSIDE the toolbar lockup, right-aligned above it:
 * matching count + Table/Groups switcher.
 */
export function FlowViewControls({
  view,
  onViewChange,
  totalCount,
  onExportCsv,
  className,
}: {
  view: FlowsViewMode;
  onViewChange: (view: FlowsViewMode) => void;
  totalCount: number | null;
  onExportCsv?: () => void;
  className?: string;
}) {
  // `PillToggle` is a shared two-state control with no ref of its own; the
  // surface's `target` redirects a click to whichever option is currently
  // INACTIVE, so `click` always flips table <-> grouped.
  const viewToggleGuide = useGuideSurface<HTMLDivElement>({
    id: 'flows.viewToggle',
    role: 'switch',
    label: 'Switch between table and grouped view',
    describe: () => ({ view }),
    target: (root) =>
      Array.from(root.querySelectorAll<HTMLButtonElement>('[data-pill-option]')).find(
        (button) => button.getAttribute('aria-checked') !== 'true'
      ) ?? null,
  });

  return (
    <div className={cn('flex items-center justify-end gap-3', className)}>
      {totalCount !== null && (
        <Text size="sm" className="text-muted-foreground whitespace-nowrap tabular-nums">
          {totalCount} {totalCount === 1 ? 'workflow' : 'workflows'}
        </Text>
      )}
      {onExportCsv && (
        <Button
          variant="icon"
          size="sm"
          iconOnly
          tooltip="Export CSV"
          onPress={onExportCsv}
        >
          <Download size={16} />
        </Button>
      )}
      <div ref={viewToggleGuide.ref}>
        <PillToggle
          aria-label="Flows view"
          value={view}
          onChange={onViewChange}
          options={VIEW_OPTIONS}
        />
      </div>
    </div>
  );
}

export function FlowFilterBar({
  searchInput,
  onSearchInputChange,
  searchParams,
  onFilterChange,
  tags,
  tagsLoading,
  filterDialogTrigger,
  activeFilterChips,
  onChipRemove,
  onClearAll,
  showClearAll,
  attached = false,
}: FlowFilterBarProps) {
  const eligibilityModel = useEligibilityModel();
  const favoritesActive = searchParams.favorites === true;

  const tagOptions: MultiSelectOption[] = React.useMemo(
    () => tags.map((t) => ({ id: t.id, label: t.name })),
    [tags]
  );

  const sortChoice = currentSortChoice(searchParams.sortBy, searchParams.sortOrder);
  const sortLabel = SORT_CHOICES.find((c) => c.id === sortChoice)?.label ?? 'Newest';
  const handleSortAction = React.useCallback(
    (key: React.Key) => {
      const choice = SORT_CHOICES.find((c) => c.id === key);
      if (!choice) return;
      onFilterChange({ sortBy: choice.sortBy, sortOrder: choice.sortOrder, page: 1 });
    },
    [onFilterChange]
  );

  const handleFavoritesToggle = React.useCallback(() => {
    onFilterChange({ favorites: favoritesActive ? undefined : true, page: 1 });
  }, [favoritesActive, onFilterChange]);

  const searchGuide = useGuideSurface<HTMLInputElement>({
    id: 'flows.search',
    role: 'searchbox',
    label: 'Search flows by name',
    describe: () => ({ value: searchInput }),
  });
  const favoritesGuide = useGuideSurface<HTMLButtonElement>({
    id: 'flows.favoritesToggle',
    role: 'switch',
    label: 'Show favorites only',
    describe: () => ({ active: favoritesActive }),
  });
  const clearFiltersGuide = useGuideSurface<HTMLButtonElement>({
    id: 'flows.clearFilters',
    role: 'button',
    label: 'Clear search and filters',
    describe: () => ({ visible: showClearAll }),
  });

  return (
    <FilterToolbar attached={attached}>
      <FilterToolbarSearch
        aria-label="Search workflows"
        placeholder="Search by name..."
        value={searchInput}
        onChange={onSearchInputChange}
        inputRef={searchGuide.ref}
      />

      <Button
        ref={favoritesGuide.ref}
        variant="ghost"
        size="sm"
        iconOnly
        onPress={handleFavoritesToggle}
        aria-label={favoritesActive ? 'Show all workflows' : 'Show favorites only'}
        aria-pressed={favoritesActive}
        tooltip={favoritesActive ? 'Showing favorites' : 'Favorites only'}
        className={cn(favoritesActive && 'bg-amber-500/10')}
      >
        <Star
          size={16}
          weight={favoritesActive ? 'fill' : 'regular'}
          className={favoritesActive ? 'text-amber-400' : 'text-muted-foreground'}
        />
      </Button>

      <ToolbarFilterMenu
        label="Tags"
        options={tagOptions}
        selectedIds={searchParams.tagIds ?? []}
        onChange={(ids) => onFilterChange({ tagIds: ids.length > 0 ? ids : undefined, page: 1 })}
        isLoading={tagsLoading}
      />
      <ToolbarFilterMenu
        label="Status"
        options={STATUS_OPTIONS}
        selectedIds={searchParams.status ?? []}
        onChange={(ids) => onFilterChange({ status: ids.length > 0 ? ids : undefined, page: 1 })}
      />
      {eligibilityModel ? (
        <ToolbarFilterMenu
          label="Eligibility"
          options={ELIGIBILITY_OPTIONS}
          selectedIds={eligibilitySelectedIds(searchParams.eligible, searchParams.paused)}
          onChange={(ids) => onFilterChange({ ...eligibilityFromIds(ids), page: 1 })}
        />
      ) : (
        <ToolbarFilterMenu
          label="Promotion"
          options={PROMOTION_OPTIONS}
          selectedIds={searchParams.promotion ?? []}
          onChange={(ids) =>
            onFilterChange({ promotion: ids.length > 0 ? ids : undefined, page: 1 })
          }
        />
      )}
      <ToolbarFilterMenu
        label="Health"
        options={HEALTH_OPTIONS}
        selectedIds={searchParams.health ?? []}
        onChange={(ids) => onFilterChange({ health: ids.length > 0 ? ids : undefined, page: 1 })}
      />
      <ToolbarFilterMenu
        label="Last run"
        options={LAST_RUN_STATUS_OPTIONS}
        selectedIds={searchParams.lastRunStatus ?? []}
        onChange={(ids) =>
          onFilterChange({ lastRunStatus: ids.length > 0 ? ids : undefined, page: 1 })
        }
      />

      <FilterToolbarDivider />

      <MenuTrigger>
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 text-muted-foreground"
          iconLeft={<ArrowsDownUp size={14} />}
        >
          {sortLabel}
        </Button>
        <Menu onAction={handleSortAction} className="min-w-[160px]">
          {SORT_CHOICES.map((choice) => (
            <MenuItem key={choice.id} id={choice.id} className="gap-2">
              <span className="flex h-3.5 w-3.5 items-center justify-center shrink-0">
                {choice.id === sortChoice && <Check size={12} weight="bold" />}
              </span>
              {choice.label}
            </MenuItem>
          ))}
        </Menu>
      </MenuTrigger>

      {filterDialogTrigger}

      {showClearAll && (
        <Button
          ref={clearFiltersGuide.ref}
          variant="ghost"
          size="sm"
          className="text-muted-foreground"
          onPress={onClearAll}
        >
          Clear
        </Button>
      )}

      <FilterToolbarChips chips={activeFilterChips} onRemove={onChipRemove} />
    </FilterToolbar>
  );
}
